<template>
  <div class="flex flex-col gap-2">
    <gallery-mobile
      v-if="mobileGallery !== null"
      :files="mobileGallery.files"
      :index="mobileGallery.index"
      @close="mobileGallery = null"
    />
    <div
      v-for="group in groups"
      :key="`source_${group.source}`"
      class="file-group"
    >
      <button
        type="button"
        class="file-group-header"
        :aria-expanded="isExpanded(group) ? 'true' : 'false'"
        @click="toggle(group)"
      >
        <div class="flex flex-col items-start min-w-0">
          <span class="font-bold truncate max-w-full">{{ group.source }}</span>
          <span v-if="group.firstTs" class="text-xs text-gray-500">
            from {{ group.firstTs | shortdate }}
          </span>
        </div>
        <div class="flex items-center gap-2 shrink-0 text-gray-500">
          <span class="text-xs">
            {{ group.files.length }} {{ group.files.length === 1 ? 'file' : 'files' }}
          </span>
          <i class="fas fa-chevron-down chevron" :class="{ 'chevron-open': isExpanded(group) }"></i>
        </div>
      </button>

      <div
        v-if="!isExpanded(group) && group.files.length > 0"
        class="file-group-strip"
      >
        <button
          v-for="(file, index) in group.files.slice(0, previewCount)"
          :key="file.id"
          type="button"
          class="strip-thumb"
          :title="file.filename"
          @click="openFile(group, index)"
        >
          <img
            v-if="thumbnailSrc(file)"
            :src="thumbnailSrc(file)"
            :alt="file.filename"
            loading="lazy"
          />
          <i v-else-if="getFileType(file) === 'video'" class="fas fa-video"></i>
          <i v-else class="far fa-file"></i>
        </button>
        <button
          v-if="group.files.length > previewCount"
          type="button"
          class="strip-thumb strip-more"
          @click="toggle(group)"
        >
          +{{ group.files.length - previewCount }}
        </button>
      </div>

      <div v-if="isExpanded(group)" class="file-group-body">
        <gallery
          :files="group.files"
          :busy="uploadingSources[group.source] > 0"
          @delete="deleteFile"
          @upload="uploadFile($event, group.source)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { getFileType } from '@/helpers';

const Gallery = () => import('@/components/global/gallery/Gallery.vue');
const GalleryMobile = () => import('@/components/global/gallery/GalleryMobile.vue');

const MOBILE_WIDTH = 768;

export default {
  name: 'CandleFileGroups',
  components: {
    Gallery,
    GalleryMobile,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    fileSources: {
      type: Array,
      required: true,
    },
  },
  computed: {
    groups() {
      const files = this.files ?? [];
      return this.fileSources.map((source) => {
        const groupFiles = files.filter((file) => file.source === source);
        const timestamps = groupFiles
          .map((file) => file.mtime_ts || file.upload_ts)
          .filter((ts) => ts > 0);
        return {
          source,
          files: groupFiles,
          firstTs: timestamps.length > 0 ? Math.min(...timestamps) : null,
        };
      });
    },
  },
  data() {
    return {
      files: null,
      previewCount: 4,
      // Collapsed by default on mobile, where long galleries are hard to scroll past
      defaultExpanded: window.innerWidth > MOBILE_WIDTH,
      expanded: {},
      uploadingSources: {},
      mobileGallery: null,
    };
  },
  methods: {
    getFileType(file) {
      return getFileType(file);
    },
    thumbnailSrc(file) {
      if (file.thumbnail_link) {
        return file.thumbnail_link;
      }
      if (getFileType(file) === 'image') {
        return file.link;
      }
      return null;
    },
    isExpanded(group) {
      return this.expanded[group.source] ?? this.defaultExpanded;
    },
    toggle(group) {
      this.$set(this.expanded, group.source, !this.isExpanded(group));
    },
    openFile(group, index) {
      if (window.innerWidth <= MOBILE_WIDTH) {
        this.mobileGallery = {
          files: group.files,
          index,
        };
        return;
      }
      this.toggle(group);
    },
    uploadFile(uploadFile, fileSource) {
      this.$set(this.uploadingSources, fileSource, (this.uploadingSources[fileSource] ?? 0) + 1);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('mtime', uploadFile.lastModified / 1000);
      formData.append('fileSource', fileSource);
      this.$http
        .post(`/candle/${this.id}/files`)
        .send(formData)
        .then((res) => {
          this.files.push(res.body.file);
        })
        .catch((err) => {
          this.$toast.error(`Failed to upload file: ${err.response.text}`);
        })
        .finally(() => {
          this.uploadingSources[fileSource]--;
        });
    },
    deleteFile(deleteFile) {
      this.$http
        .delete(`/candle/${this.id}/files/${deleteFile.id}`)
        .then(() => {
          this.files = this.files.filter((file) => file.id !== deleteFile.id);
          this.$emit('deleted', deleteFile.id);
        })
        .catch((err) => {
          this.$toast.error(`Failed to delete file: ${err.response.text}`);
        });
    },
    fetchFiles() {
      this.$http
        .get(`/candle/${this.id}/files`)
        .then((res) => {
          this.files = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch files: ${err.response.text}`);
        });
    },
  },
  watch: {
    id: {
      immediate: true,
      handler() {
        this.expanded = {};
        this.fetchFiles();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.file-group {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background-color: #fff;
  overflow: hidden;
}

.file-group-header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  text-align: left;
  background: none;
  border: 0;

  &:hover {
    background-color: #f9fafb;
  }
}

.chevron {
  transition: transform 0.15s ease;
}

.chevron-open {
  transform: rotate(180deg);
}

.file-group-strip {
  display: flex;
  gap: 6px;
  padding: 0 12px 10px;
}

.strip-thumb {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background-color: #eee;
  color: #6b7280;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  i {
    font-size: 20px;
  }
}

.strip-more {
  font-weight: 600;
  font-size: 14px;
}

.file-group-body {
  padding: 0 12px 12px;

  // Gallery needs ~334px for two columns, keep the gutter thin on phones
  @media (max-width: 768px) {
    padding: 0 4px 4px;
  }
}
</style>
